const localStorageKey = 'storeeel_targeting_segments';
const DEFAULT_TTL_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(localStorageKey) || '{}');
  } catch {
    localStorage.removeItem(localStorageKey);
    return {};
  }
};

const persist = (value) => {
  localStorage.setItem(localStorageKey, JSON.stringify(value));
};

const asArray = (value) => Array.isArray(value) ? value : [];

const normalizeValues = (values) => {
  const resolvedValues = Array.isArray(values)
    ? values
    : (values && typeof values === 'object')
      ? Object.values(values)
      : [values];
  return [...new Set(resolvedValues.map((value) => String(value)).filter(Boolean))];
};

const normalizeQuestionValues = (values) => {
  const resolved = normalizeValues(values);
  return resolved.length > 0 ? resolved[0] : '';
};

const normalizeQuestionValueMap = (values) => {
  if (!values || Array.isArray(values) || typeof values !== 'object') return {};

  return Object.entries(values).reduce((result, [questionKey, questionValue]) => {
    const normalizedKey = String(questionKey || '');
    const normalizedValue = normalizeQuestionValues(questionValue);
    if (!normalizedKey || !normalizedValue) return result;
    result[normalizedKey] = normalizedValue;
    return result;
  }, {});
};

const resolveTtlDays = (resetDays) => {
  const resolved = Number(resetDays);
  return Number.isFinite(resolved) && resolved > 0 ? resolved : DEFAULT_TTL_DAYS;
};

const buildQuestionnaireMap = (questionnaireGroups = []) => (
  asArray(questionnaireGroups).reduce((result, group) => {
    if (String(group?.groupType || '') !== 'segment') return result;
    const questionKey = String(group?.segmentKeyId || '');
    if (!questionKey) return result;
    result[questionKey] = group;
    return result;
  }, {})
);

const sanitizeEntryMap = (entries, now = Date.now()) => Object.entries(entries && typeof entries === 'object' ? entries : {}).reduce((result, [questionKey, entry]) => {
  const normalizedKey = String(questionKey || '');
  const normalizedValue = normalizeQuestionValues(entry?.values);
  if (!normalizedKey || !entry?.TTL || entry.TTL <= now || !normalizedValue) return result;
  result[normalizedKey] = {
    TTL: entry.TTL,
    values: [normalizedValue],
  };
  return result;
}, {});

export const targetingSegmentRepository = {
  get(tenantId) {
    const tenantKey = String(tenantId || 'default');
    const data = load();
    const entry = data?.[tenantKey];

    if (entry?.entries && typeof entry.entries === 'object') {
      const sanitizedEntries = sanitizeEntryMap(entry.entries);
      const sanitizedValues = Object.entries(sanitizedEntries).reduce((result, [questionKey, info]) => {
        result[questionKey] = info.values[0];
        return result;
      }, {});

      if (Object.keys(sanitizedEntries).length !== Object.keys(entry.entries || {}).length) {
        if (Object.keys(sanitizedEntries).length > 0) {
          data[tenantKey] = { entries: sanitizedEntries };
        } else {
          delete data[tenantKey];
        }
        persist(data);
      }

      return sanitizedValues;
    }

    if (!entry?.TTL || entry.TTL <= Date.now()) {
      if (entry) {
        delete data[tenantKey];
        persist(data);
      }
      return [];
    }

    return normalizeValues(entry.values);
  },

  set(tenantId, values, questionnaireGroups = []) {
    const tenantKey = String(tenantId || 'default');
    const data = load();
    const questionValueMap = normalizeQuestionValueMap(values);

    if (Object.keys(questionValueMap).length === 0) {
      const existingValues = normalizeValues(data?.[tenantKey]?.values);
      data[tenantKey] = {
        TTL: Date.now() + DEFAULT_TTL_DAYS * DAY_MS,
        values: normalizeValues([...existingValues, ...normalizeValues(values)]),
      };
      persist(data);
      return;
    }

    const questionnaireMap = buildQuestionnaireMap(questionnaireGroups);
    const existingEntries = sanitizeEntryMap(data?.[tenantKey]?.entries);

    Object.entries(questionValueMap).forEach(([questionKey, questionValue]) => {
      const resetDays = resolveTtlDays(questionnaireMap[questionKey]?.questionnaire?.resetDays);
      existingEntries[questionKey] = {
        TTL: Date.now() + resetDays * DAY_MS,
        values: [questionValue],
      };
    });

    data[tenantKey] = { entries: existingEntries };

    persist(data);
  },

  removeExpired() {
    const data = load();
    const now = Date.now();
    let updated = false;

    Object.entries(data).forEach(([tenantId, entry]) => {
      if (entry?.entries && typeof entry.entries === 'object') {
        const sanitizedEntries = sanitizeEntryMap(entry.entries, now);
        if (Object.keys(sanitizedEntries).length === 0) {
          delete data[tenantId];
        } else if (Object.keys(sanitizedEntries).length !== Object.keys(entry.entries).length) {
          data[tenantId] = { entries: sanitizedEntries };
        } else {
          return;
        }
        updated = true;
        return;
      }

      if (!entry?.TTL || entry.TTL <= now) {
        delete data[tenantId];
        updated = true;
      }
    });

    if (updated) persist(data);
  },
};
